import type { APIResponse, SupportedCurrency } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

export interface SubscriptionData {
    id?: string
    plan: string
    status?: string
    provider?: string
    eventsUsed?: number
    eventsLimit?: number
    aiCreditsUsed?: number
    aiCreditsLimit?: number
    aiAddon: number
    cancelAtPeriodEnd?: number
    currentPeriodStart?: string
    currentPeriodEnd?: string
    currency?: SupportedCurrency
}

export interface UsageData {
    plan: string
    /** Billing period: UTC calendar month (YYYY-MM). */
    period: string
    periodStart: string
    periodEnd: string
    /** Exact events this period (not sampled), including managed accounts (partners). */
    eventsUsed: number
    ownEvents?: number
    /** Partner-managed orgs billed on this account; null when there are none. */
    managedAccounts?: { count: number; events: number; aiRuns: number } | null
    freeEventsIncluded: number
    freeEventsUsed: number
    freeEventsRemaining: number
    billableEvents: number
    estimatedCostCents: number
    aiAddon: number
    // AI cost breakdown
    aiCreditsUsed: number
    aiRetainerCents: number
    aiRunCostCents: number
    // Forecast
    forecastEventCount: number
    forecastAiRuns: number
    forecastCostCents: number
    dailyAverage: number
    daysElapsed: number
    daysInPeriod: number
    /** Same estimate in the organization's local currency (billing stays in `currency`). */
    localEstimate: { currency: SupportedCurrency; costCents: number; forecastCents: number } | null
    /** What the hourly usage reporter has sent to the payment provider this period. */
    reported: {
        events: number
        pending: number
        lastReportedAt: string | null
        lastError: string | null
    }
    limits: {
        projects: { used: number; max: number | null }
        members: { max: number | null }
        retentionDays: { default: number; max: number }
        monthlyEventCap: number | null
        dailyEventCap: number | null
        burstEventsPerMinute: number
    }
    latestInvoice: { url: string; status: string; amountCents: number; currency: string } | null
    // Billing provider + currency (the charged currency: USD on Stripe)
    country: string
    billingProvider: 'stripe' | 'razorpay'
    currency: SupportedCurrency
}

export interface InvoiceData {
    id: string
    provider: string
    providerInvoiceId: string
    amountCents: number
    currency: string
    status: string
    periodStart?: string
    periodEnd?: string
    paidAt?: string
    hostedUrl?: string
    pdfUrl?: string
    createdAt: string
    // Tax/invoice detail fields
    invoiceNumber?: string
    subtotalCents?: number
    taxCents?: number
    taxLabel?: string
    taxRate?: number
    customerCountry?: string
    description?: string
}

export interface CheckoutInput {
    country?: string
    successUrl: string
    cancelUrl: string
}

export interface CheckoutResult {
    url: string
    sessionId?: string
    subscriptionId?: string
    provider: string
    razorpayKeyId?: string
}

export const billingApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getSubscription: builder.query<SubscriptionData | null, void>({
            query: () => '/billing/subscription',
            transformResponse: (res: APIResponse<SubscriptionData | null>) => res.data ?? null,
            providesTags: ['Billing'],
        }),

        getUsage: builder.query<UsageData, void>({
            query: () => '/billing/usage',
            transformResponse: (res: APIResponse<UsageData>) => res.data!,
            providesTags: ['Billing'],
        }),

        getInvoices: builder.query<InvoiceData[], void>({
            query: () => '/billing/invoices',
            transformResponse: (res: APIResponse<InvoiceData[]>) => res.data ?? [],
            providesTags: ['Billing'],
        }),

        createCheckout: builder.mutation<CheckoutResult, CheckoutInput>({
            query: body => ({ url: '/billing/checkout', method: 'POST', body }),
            transformResponse: (res: APIResponse<CheckoutResult>) => res.data!,
        }),

        createPortalSession: builder.mutation<
            { url?: string; provider: string; inApp?: boolean },
            { returnUrl: string }
        >({
            query: body => ({ url: '/billing/portal', method: 'POST', body }),
            transformResponse: (
                res: APIResponse<{ url?: string; provider: string; inApp?: boolean }>,
            ) => res.data!,
        }),

        cancelSubscription: builder.mutation<
            { message: string },
            { atPeriodEnd?: boolean; reason?: string }
        >({
            query: body => ({ url: '/billing/cancel', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ message: string }>) => res.data!,
            invalidatesTags: ['Billing'],
        }),

        toggleAiAddon: builder.mutation<{ aiAddon: boolean }, { enabled: boolean }>({
            query: body => ({ url: '/billing/ai-addon', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ aiAddon: boolean }>) => res.data!,
            invalidatesTags: ['Billing'],
        }),

        verifyPayment: builder.mutation<
            { activated: boolean },
            {
                razorpayPaymentId: string
                razorpaySubscriptionId: string
                razorpaySignature: string
            }
        >({
            query: body => ({ url: '/billing/verify-payment', method: 'POST', body }),
            transformResponse: (res: APIResponse<{ activated: boolean }>) => res.data!,
            invalidatesTags: ['Billing'],
        }),
    }),
})

export const {
    useGetSubscriptionQuery,
    useGetUsageQuery,
    useGetInvoicesQuery,
    useCreateCheckoutMutation,
    useCreatePortalSessionMutation,
    useCancelSubscriptionMutation,
    useToggleAiAddonMutation,
    useVerifyPaymentMutation,
} = billingApi
